import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, buildUrl } from "@shared/routes";
import { InsertProspect } from "@shared/schema";
import { authFetch } from "@/lib/authFetch";

export function useProspects() {
  return useQuery({
    queryKey: [api.prospects.list.path],
    queryFn: async () => {
      const res = await authFetch(api.prospects.list.path);
      if (!res.ok) throw new Error("Failed to fetch prospects");
      return res.json();
    },
  });
}

export function useProspect(id: string) {
  return useQuery({
    queryKey: [api.prospects.get.path, id],
    queryFn: async () => {
      const url = buildUrl(api.prospects.get.path, { id });
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Failed to fetch prospect");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateProspect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertProspect) => {
      const res = await authFetch(api.prospects.create.path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create prospect");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.prospects.list.path] }),
  });
}

export function useUpdateProspect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<InsertProspect>) => {
      const url = buildUrl(api.prospects.update.path, { id });
      const res = await authFetch(url, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update prospect");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.prospects.list.path] }),
  });
}

export function useTransformProspect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, type }: { id: string; type: "client" | "revendeur" }) => {
      const url = buildUrl(api.prospects.transform.path, { id });
      const res = await authFetch(url, {
        method: "POST",
        body: JSON.stringify({ type }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to transform prospect");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.prospects.list.path] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/resellers"] });
    },
  });
}
